// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { BillingPlanListItem, Charge } from '@/billing/api/types';
import { useConfirmBillingPlan } from '@/billing/hooks/use-confirm-billing-plan';
import { FormDatePicker } from '@/components/form-date-picker';
import {
  MAXIMUM_DATE,
  MINIMUM_DATE,
  applyNullableApplicationDateRange,
} from '@/constants/date-shortcut';
import {
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { useProjectLineItemCharges } from '@/project/hooks/use-project-line-item-charges';
import {
  ChargeFormDateValue,
  chargeDateKey,
} from '@/project/utilities/charge-date';
import {
  CascadeChargeDatesParams,
  ChargeFormValue,
  cascadeChargeDates,
} from '@/project/utilities/generate-charges';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const activationDateSchema = applyNullableApplicationDateRange(
  z
    .custom<DateTime>((value) => DateTime.isDateTime(value), {
      message: 'An activation date is required',
    })
    .nullable()
    .refine((value) => value !== null && value.isValid, {
      message: 'An activation date is required',
    }),
  'Activation date'
);

const confirmFormSchema = z.object({
  activationDate: activationDateSchema,
});

type ConfirmFormData = z.infer<typeof confirmFormSchema>;

interface Props {
  isOpen: boolean;
  plan: null | BillingPlanListItem;
  onClose: () => void;
}

// Confirms a Pending billing plan from the listing's row triple-dot
// menu. The user supplies an activation date; the live preview shows
// the cascaded labels/dates. On submit the dialog forwards a sparse
// label-override list keyed by charge key so the BE persists the
// labels the user just previewed (BE owns the cascade math).
export function ConfirmBillingPlanDialog(props: Props) {
  const [submitError, setSubmitError] = useState<null | string>(null);
  const [chargesOpen, setChargesOpen] = useState(false);
  const backendActivationDateErrorValue = useRef<string | null>(null);

  const chargesQuery = useProjectLineItemCharges(
    props.plan?.projectKey ?? '',
    props.isOpen && props.plan ? props.plan.lineItemKey : null
  );
  const charges: null | Charge[] = chargesQuery.data?.data ?? null;

  // Default activation date is the plan's start (date-only).
  const initialActivationDate = useMemo(() => {
    if (!props.isOpen || !props.plan) {
      return null;
    }
    return props.plan.startOn ?? DateTime.now().startOf('day');
  }, [props.isOpen, props.plan]);

  const {
    control,
    handleSubmit,
    clearErrors,
    reset,
    setError,
    formState: { errors },
  } = useForm<ConfirmFormData>({
    resolver: zodResolver(confirmFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: { activationDate: initialActivationDate },
    values: { activationDate: initialActivationDate },
  });

  useEffect(() => {
    if (!props.isOpen || !props.plan) {
      return;
    }
    backendActivationDateErrorValue.current = null;
    setSubmitError(null);
    setChargesOpen(false);
  }, [props.isOpen, props.plan]);

  // Triggered after the dialog has fully animated closed (MUI
  // TransitionProps.onExited). Clears submit error + collapsed
  // charges + form errors so a same-plan reopen shows a fresh state.
  const handleExited = () => {
    backendActivationDateErrorValue.current = null;
    setSubmitError(null);
    setChargesOpen(false);
    reset();
  };

  const close = () => {
    props.onClose();
  };

  const activationDateInput = useWatch({ control, name: 'activationDate' });
  const activationDateKey = chargeDateKey(activationDateInput);

  // Clear local validation errors as soon as the field becomes valid.
  // Backend field errors stay visible until the user changes the date.
  useEffect(() => {
    if (!errors.activationDate) {
      return;
    }

    if (errors.activationDate.type === 'manual') {
      if (backendActivationDateErrorValue.current !== activationDateKey) {
        backendActivationDateErrorValue.current = null;
        clearErrors('activationDate');
      }
      return;
    }

    if (activationDateSchema.safeParse(activationDateInput).success) {
      clearErrors('activationDate');
    }
  }, [
    activationDateInput,
    activationDateKey,
    errors.activationDate,
    clearErrors,
  ]);

  const previewedCharges = useMemo<null | ChargeFormValue[]>(
    () => buildPreviewedCharges(props.plan, charges, activationDateInput),
    [props.plan, charges, activationDateInput]
  );

  const { showToast } = useToast();
  const confirmMutation = useConfirmBillingPlan({
    onSuccess: (chargesScheduled) => {
      showToast({
        severity: 'success',
        element: (
          <span>
            Plan confirmed.{' '}
            {pluralize(chargesScheduled, { singular: 'charge' })} scheduled.
          </span>
        ),
      });
      close();
    },
    onError: (message, apiStatusCode) => {
      if (
        apiStatusCode ===
        ERROR_CODE_MAPPING.billing_plan_activation_date_out_of_range
      ) {
        backendActivationDateErrorValue.current = activationDateKey;
        setError('activationDate', {
          type: 'manual',
          message,
        });
        return;
      }
      setSubmitError(message);
    },
  });

  const onSubmit = (values: ConfirmFormData) => {
    if (!props.plan || planStart === null || planEnd === null) {
      return;
    }
    setSubmitError(null);

    if (values.activationDate === null || !values.activationDate.isValid) {
      setSubmitError('Activation date is invalid');
      return;
    }

    // Sparse label overrides: only forward labels for charges with a
    // server key (cascade preview ran on a loaded charge list).
    // BE owns the cascade math; this list rewrites labels only.
    const labelOverrides: { key: string; label: string }[] =
      previewedCharges
        ?.filter((c): c is ChargeFormValue & { key: string } => c.key !== null)
        .map((c) => ({ key: c.key, label: c.label })) ?? [];

    confirmMutation.mutate({
      projectKey: props.plan.projectKey,
      lineItemKey: props.plan.lineItemKey,
      startOn: planStart,
      endOn: planEnd,
      activationDate: values.activationDate,
      chargeLabelOverrides: labelOverrides,
    });
  };

  const totalCharges =
    (props.plan?.plannedCount ?? 0) +
    (props.plan?.billableCount ?? 0) +
    (props.plan?.invoicedCount ?? 0);
  const planStart = props.plan?.startOn ?? null;
  const planEnd = props.plan?.endOn ?? null;
  const submitDisabled = !props.plan || planStart === null || planEnd === null;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      TransitionProps={{ onExited: handleExited }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          Activate billing plan
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {props.plan && (
            <Stack
              spacing={1}
              sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
            >
              <ContextRow
                label="Customer"
                value={props.plan.customerName ?? '—'}
              />
              <ContextRow label="Project" value={props.plan.projectName} />
              <ContextRow
                label="Line item"
                value={`${props.plan.itemSku} - (${props.plan.itemName})`}
              />
              <ContextRow
                label="Quantity"
                value={formatNumber(props.plan.quantity)}
              />
              <ContextRow
                label="Service period start"
                value={planStart?.toISODate() ?? '—'}
              />
              <ContextRow
                label="Service period end"
                value={planEnd?.toISODate() ?? '—'}
              />
              <ContextRow
                label="Line item total"
                value={formatCurrencyValue(props.plan.contractValue)}
              />
            </Stack>
          )}

          <Stack spacing={2}>
            <FormDatePicker
              name="activationDate"
              control={control}
              label="Activation date"
              minimumDate={MINIMUM_DATE}
              maximumDate={MAXIMUM_DATE}
            />

            {props.plan && (
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => setChargesOpen((prev) => !prev)}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Charges
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">
                      {pluralize(totalCharges, { singular: 'charge' })}
                    </Typography>
                    {chargesOpen ? (
                      <ExpandLessRounded fontSize="small" />
                    ) : (
                      <ExpandMoreRounded fontSize="small" />
                    )}
                  </Stack>
                </Stack>
                <Collapse in={chargesOpen} unmountOnExit>
                  <Box
                    sx={{
                      mt: 1.5,
                      maxHeight: 240,
                      overflowY: 'auto',
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {chargesQuery.isError ? (
                      <Alert severity="error" sx={{ m: 1 }}>
                        Could not load charges for this plan. Try closing and
                        reopening the dialog.
                      </Alert>
                    ) : charges === null ? (
                      <Stack spacing={0.5} sx={{ p: 1 }}>
                        <Skeleton variant="rounded" height={24} />
                        <Skeleton variant="rounded" height={24} />
                        <Skeleton variant="rounded" height={24} />
                      </Stack>
                    ) : charges.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2, textAlign: 'center' }}
                      >
                        No charges on this plan yet
                      </Typography>
                    ) : (
                      <ChargesPreviewTable
                        charges={charges}
                        previewed={previewedCharges}
                      />
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Stack>
          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button onClick={close} disabled={confirmMutation.isPending}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={confirmMutation.isPending}
            disabled={submitDisabled}
          >
            Activate plan
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function ContextRow(props: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="caption" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="body2">{props.value}</Typography>
    </Stack>
  );
}

function ChargesPreviewTable(props: {
  charges: Charge[];
  previewed: null | ChargeFormValue[];
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Label</TableCell>
          <TableCell>Service period</TableCell>
          <TableCell align="right">Amount</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.charges.map((charge, index) => {
          const preview = props.previewed?.[index] ?? null;
          const label = preview?.label ?? charge.label;
          const start =
            preview?.servicePeriodStart ?? charge.servicePeriodStart;
          const end = preview?.servicePeriodEnd ?? charge.servicePeriodEnd;
          return (
            <TableRow key={charge.key}>
              <TableCell>{label}</TableCell>
              <TableCell>{formatChargePeriod(start, end)}</TableCell>
              <TableCell align="right">
                {formatCurrencyValue(charge.totalPrice)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Build the live preview + the labels we'll forward at submit time.
// Returns null whenever a precondition is missing — charges not
// loaded, plan missing required frequency fields, or count mismatch.
function buildPreviewedCharges(
  plan: null | BillingPlanListItem,
  charges: null | Charge[],
  activationDate: DateTime<boolean> | null
): null | ChargeFormValue[] {
  if (!plan || charges === null || charges.length === 0) {
    return null;
  }
  if (
    plan.startOn === null ||
    plan.endOn === null ||
    plan.chargeFrequency === null ||
    plan.invoiceFrequency === null ||
    plan.invoiceMode === null
  ) {
    return null;
  }
  if (activationDate === null || activationDate.toISODate() === null) {
    return null;
  }
  const params: CascadeChargeDatesParams = {
    startOn: plan.startOn,
    endOn: plan.endOn,
    activationDate,
    chargeFrequency: plan.chargeFrequency,
    invoiceFrequency: plan.invoiceFrequency,
    invoiceMode: plan.invoiceMode,
    charges: charges.map(toChargeFormValue),
  };
  return cascadeChargeDates(params);
}

function toChargeFormValue(charge: Charge): ChargeFormValue {
  return {
    id: charge.id,
    key: charge.key,
    label: charge.label,
    pricingMode: charge.pricingMode,
    quantity: charge.quantity,
    unitPrice:
      charge.unitPrice === null
        ? null
        : toSerializableCurrency(charge.unitPrice),
    totalPrice: toSerializableCurrency(charge.totalPrice),
    servicePeriodStart: charge.servicePeriodStart,
    servicePeriodEnd: charge.servicePeriodEnd,
    expectedBillableDate: charge.expectedBillableDate,
    billingEligibleDate: charge.billingEligibleDate,
    status: charge.status,
    isCustomModified: charge.isCustomModified,
    triggerType: charge.triggerType,
    sequence: charge.sequence,
  };
}

function formatChargePeriod(
  start: ChargeFormDateValue,
  end: ChargeFormDateValue
): string {
  const startKey = chargeDateKey(start);
  const endKey = chargeDateKey(end);
  if (startKey === null && endKey === null) return '—';
  if (startKey === null) return `– ${endKey ?? ''}`;
  if (endKey === null) return `${startKey} –`;
  return `${startKey} – ${endKey}`;
}
